import * as React from 'react';
import Link from 'next/link';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * MetricCard — THE standard KPI/stat tile.
 *
 * Promoted from the admin dashboard's local StatCard (audit found four
 * competing KPI-tile implementations, plus a dead shared/stat-card twin).
 * Trend colors use the semantic feedback tokens, not raw palette classes.
 *
 *   <MetricCard title="Active Jobs" value={12} icon={Briefcase} />
 *   <MetricCard title="Applications" value={230} change="+12% this week" trend="up" />
 *   <MetricCard title="Pending" value={4} href="/admin/vault?filter=pending" />
 */

export interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value: React.ReactNode;
  /** Small line under the value — delta ("+12%") or context ("Target <2%"). */
  change?: React.ReactNode;
  /** Directional TREND (arrow). Only for temporal deltas — for static
   *  health/threshold coloring use `valueTone` instead. */
  trend?: 'up' | 'down' | 'neutral';
  /** Colors the value itself for threshold/health semantics (no arrow). */
  valueTone?: 'success' | 'warning' | 'destructive';
  icon?: React.ComponentType<{ className?: string }>;
  /** Renders a skeleton in place of the value. */
  loading?: boolean;
  /** Makes the whole card a link (drill-in to the filtered list). */
  href?: string;
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(
  ({ title, value, change, trend = 'neutral', valueTone, icon: Icon, loading = false, href, className, ...props }, ref) => {
    const card = (
      <Card
        ref={ref}
        className={cn(href && 'transition-colors hover:border-primary/40 hover:shadow-md', className)}
        {...props}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              {loading ? (
                <Skeleton className="mt-2 h-8 w-20" />
              ) : (
                <p
                  className={cn(
                    'text-3xl font-bold mt-1',
                    valueTone === 'success' && 'text-success',
                    valueTone === 'warning' && 'text-warning',
                    valueTone === 'destructive' && 'text-destructive',
                  )}
                >
                  {value}
                </p>
              )}
              {change && !loading && (
                <p
                  className={cn(
                    'text-sm mt-1 flex items-center gap-1',
                    trend === 'up' && 'text-success',
                    trend === 'down' && 'text-destructive',
                    trend === 'neutral' && 'text-muted-foreground',
                  )}
                >
                  {trend === 'up' && <TrendingUp className="w-3 h-3" aria-hidden="true" />}
                  {trend === 'down' && <TrendingDown className="w-3 h-3" aria-hidden="true" />}
                  {change}
                </p>
              )}
            </div>
            {Icon && (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-primary" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );

    if (!href) return card;
    return (
      <Link
        href={href}
        className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {card}
      </Link>
    );
  },
);
MetricCard.displayName = 'MetricCard';

export { MetricCard };
